/**
 * The context a dynamic info panel sends with its query set.
 *
 * It must carry ONLY the keys that panel's query actually reads.
 *
 * WHY, because this caused a visible bug. template/querySet.vue watches `context` with
 * deep: true and refetches whenever its VALUES change. info.vue passed the whole form, so every
 * keystroke in an unrelated field looked like a change: typing a surname on the owner form fired
 * one /v2/data/queryset/6 request per letter and the panel flickered through its loader each
 * time. The request log showed Surname="A Fensham * Assoc", "...Associ", "...Associa" and so on,
 * one request each.
 *
 * Narrowing is what makes the guard in querySet.vue work. That guard compares by VALUE against a
 * snapshot rather than by reference, so a fresh object whose values have not changed
 * short-circuits and no fetch happens. Returning a new object here is therefore safe; returning
 * the whole form is not.
 *
 * A panel that is not listed keeps the previous behaviour and receives the whole form, so this
 * cannot change a screen that has not been checked.
 */

/** Panel name (lower case) to the context keys its query consumes. */
const PANEL_CONTEXT_KEYS: Record<string, readonly string[]> = {
  // meta.query 11 OwnerUnits is: SELECT uv.* FROM public.unit_view uv WHERE owner_id = @Id
  // so Id is the only parameter it binds. OwnerId is carried alongside it because the panel
  // component reads that name, and because a caller that has already set OwnerId should keep it.
  ownerunits: ["Id", "OwnerId"],
};

export function panelQueryContext(
  auxInputType: string | null | undefined,
  base: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const record = base ?? {};
  const keys = PANEL_CONTEXT_KEYS[(auxInputType || "").toLowerCase()];
  if (!keys) return record;

  const context: Record<string, unknown> = {};
  for (const key of keys) {
    if (record[key] != null) context[key] = record[key];
  }

  // The owner form's primary key is Id; the panel asks for OwnerId (PFT-384).
  if (context.OwnerId == null && context.Id != null) {
    context.OwnerId = context.Id;
  }
  return context;
}
